import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { FullForm, useChangeable } from '../CommonComponents/Form';

import {
    createIntegration,
    deleteIntegration,
    getIntegration,
    updateIntegration
} from '../../api/integrations';

import toast from '../../utils/toast';
import TransportorenOpterForm from './SettingsFormFields/TransportorenOpterForm';
import LogtradeForm from './SettingsFormFields/LogtradeForm';
import C3cForm from './SettingsFormFields/C3cForm';
import OpterForm from './SettingsFormFields/OpterForm';
import parseIntegrationType, {
    TRANSPORTOREN_OPTER,
    TCHALLAND_OPTER,
    SNABBUD_OPTER,
    LOGTRADE,
    C3C,
    OPTER_V1
} from './integrationTypes';

export default function IntegrationForm() {
    const { id } = useParams();
    const [type, setType] = React.useState('');
    const [settings, onChangeSettings] = useChangeable({});
    const [disableEdit, setDisableEdit] = React.useState(false);
    const navigate = useNavigate();

    React.useEffect(() => {
        (async () => {
            if (id) {
                const integration = await getIntegration(id);
                setType(integration.type);
                onChangeSettings(integration.settings);
            }
        })();
    }, [id, onChangeSettings]);

    React.useEffect(() => {
        if (type === LOGTRADE && settings.status !== 'COMPLETE') {
            setDisableEdit(true);
        } else {
            setDisableEdit(false);
        }
    }, [settings, type]);

    const onSubmit = React.useCallback(async () => {
        try {
            if (id) {
                await updateIntegration(id, { settings });
                toast('Integration uppdaterad');
                navigate(-1);
            } else {
                const createdIntegration = await createIntegration({ type, settings });
                toast('Integration skapad');
                navigate(`/integrations/${createdIntegration.id}`, { replace: true });
            }
        } catch (e: any) {
            toast(e, `Kunde inte ${id ? 'uppdatera' : 'skapa'} integrationen, försök igen`);
        }
    }, [id, settings, navigate, type]);

    const onDelete = React.useCallback(async () => {
        try {
            if (id) {
                await deleteIntegration(id);
                toast('Integration borttagen');
                navigate('/integrations', { replace: true });
            }
        } catch (e: any) {
            toast(e, 'Kunde inte ta bort integrationen, försök igen');
        }
    }, [id, navigate]);

    return (
        <FullForm
            title={`${id ? 'Redigera' : 'Skapa'} integration: ${parseIntegrationType(type)}`}
            entity={settings}
            onSubmit={onSubmit}
            submitDisabled={disableEdit}
            submitDisabledToolTip={type === LOGTRADE
                ? 'Integrationen måste vara godkänd i Logtrade innan du kan redigera den'
                : ''}
            onDelete={id ? onDelete : undefined}
            showBottomSave
        >
            {(
                type === TRANSPORTOREN_OPTER
                || type === TCHALLAND_OPTER
                || type === SNABBUD_OPTER
            ) && (
                <TransportorenOpterForm
                    settings={settings}
                    onChangeSettings={onChangeSettings}
                />
            )}
            {(
                type === LOGTRADE
            ) && (
                <LogtradeForm
                    settings={settings}
                    onChangeSettings={onChangeSettings}
                />
            )}
            {(
                type === C3C
            ) && (
                <C3cForm
                    integrationId={id}
                    settings={settings}
                    onChangeSettings={onChangeSettings}
                />
            )}
            {(
                type === OPTER_V1
            ) && (
                <OpterForm
                    integrationId={id}
                    settings={settings}
                    onChangeSettings={onChangeSettings}
                />
            )}
        </FullForm>
    );
}
